import { useCallback, useEffect, useState } from 'react';

import {
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  Typography,
  CircularProgress,
  Link,
} from '@mui/material';
import scssVariables from '@styles/variables.module.scss';
import { useStore } from '@tanstack/react-form';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { z } from 'zod';

import styles from './ExpenseDetails.module.scss';
import { validateVatAmountAgainstAmountIncludingVat } from './utils';
import { CreateNewSupplierModal } from '../../components/CreateNewSupplierModal';
import { useExpenseFormBlurredValuesStore } from '../../stores/expenseFormBlurredValues';
import { ExpenseFormType } from '../../types';
import { ProjectInformationFormFields } from '../ProjectInformation';
import { useGetUserMe } from '@/api/REST/userMe';
import {
  CurrencyCodeSelectOption,
  getCurrencyCodeSelectOptionById,
  getSearchCurrencyCodeSelectOptions,
} from '@/api/typesense/currencyCodes';
import {
  type ExpenseTypeSelectOption,
  getExpenseTypeSelectOptionById,
  getSearchExpenseTypeSelectOptions,
} from '@/api/typesense/expenseTypes';
import {
  getPaymentMethodSelectOptionById,
  getSearchPaymentMethodSelectOptions,
  PaymentMethodSelectOption,
} from '@/api/typesense/paymentMethods';
import {
  getSearchSupplierSelectOptions,
  getSupplierSelectOptionById,
} from '@/api/typesense/suppliers';
import { TypesenseCollection } from '@/api/typesense/types';
import { Icon } from '@/components/ui/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { InputFieldSkeletonLoader } from '@/components/ui/InputFieldSkeletonLoader';
import { uiIcons } from '@/constants/uiIcons';
import { isRequired } from '@/forms/schemas';
import {
  optionalNumber,
  optionalSelectOption,
  requiredNumber,
  requiredSelectOption,
} from '@/forms/schemas/definitions';
import { BaseSelectOption } from '@/forms/types';
import { withFieldGroup } from '@/forms/useAppForm';
import { useConvertCurrencies } from '@/hooks/useConvertCurrencies';
import { useFormatNumber } from '@/hooks/useFormatNumber';
import { useUserAccountHasFeature } from '@/hooks/useUserAccountHasFeature';
import { useUserAccountSettingValue } from '@/hooks/useUserAccountSettingValue';
import { parseLocaleNumber } from '@/utils/parseLocaleNumber';

export type ExpenseDetailsFormFields = {
  date: Date;
  supplier?: BaseSelectOption;
  supplierInvoiceNumber?: string;
  paymentMethod: PaymentMethodSelectOption;
  amountIncludingVat: string;
  vatAmount: string;
  expenseCurrency: CurrencyCodeSelectOption;
  expenseType: ExpenseTypeSelectOption;
  billable: boolean | null;
  targetCurrencyConvertedAmount?: string;
  expenseComment?: string;
  expenseNumber?: string;
};
export const expenseDetailsDefaultValues: ExpenseDetailsFormFields = {
  date: new Date(),
  supplier: null,
  supplierInvoiceNumber: '',
  paymentMethod: null,
  amountIncludingVat: '',
  vatAmount: '',
  expenseCurrency: null,
  expenseType: null,
  billable: null,
  targetCurrencyConvertedAmount: '',
  expenseComment: '',
  expenseNumber: '',
};

export const expenseDetailsSchema = z
  .object({
    date: z.date('validationErrors.Date'),
    supplier: optionalSelectOption(),
    supplierInvoiceNumber: z.string().optional(),
    paymentMethod: requiredSelectOption(),
    amountIncludingVat: requiredNumber(),
    vatAmount: requiredNumber(),
    expenseCurrency: requiredSelectOption({
      isoCode: z.string(),
    }),
    expenseType: requiredSelectOption({
      vat: z.number(), // Add the vat field that ExpenseTypeSelectOption requires
    }),
    billable: z.boolean().nullable(),
    targetCurrencyConvertedAmount: optionalNumber(),
    expenseComment: z.string().optional(),
    expenseNumber: z.string().optional(),
  })
  // use superRefine here to reuse the parsed numbers, even though we break after one issue (we only display one error at a time anyway)
  .superRefine((data, ctx) => {
    validateVatAmountAgainstAmountIncludingVat(
      data.amountIncludingVat,
      data.vatAmount,
    ).forEach((issue) => {
      ctx.addIssue({ code: 'custom', ...issue });
    });
  });

export type ExpenseDetailsOutput = z.infer<typeof expenseDetailsSchema>;

const TargetCurrencyConvertedAmountHelperText = ({
  expenseAmount,
  expenseCurrency,
  date,
  targetCurrency,
}: {
  expenseAmount: string;
  expenseCurrency: CurrencyCodeSelectOption;
  date: Date;
  targetCurrency: CurrencyCodeSelectOption;
}) => {
  const { t } = useTranslation('expenses');
  const { user: userAuthData } = useAuth();

  const baseAmount = 100; // Using a fixed base amount for illustration

  const { data: userMeData, isLoading: isLoadingUserMe } = useGetUserMe();

  const userTenantName = userAuthData?.profile?.idp as string | undefined; // idp matches the tenant name from first part of sub, we use in Posthog identify

  const requestExchangeRateLink = useCallback(() => {
    const url = new URL(
      'https://17i92.share.hsforms.com/2YchjxvuQSW2yiKWRL4irrw',
    );
    url.searchParams.set('severity', 'Low');
    url.searchParams.set(
      'subject',
      `New Currency Request: ${expenseCurrency?.isoCode}`,
    );
    url.searchParams.set('type_ticket', "Something's not right");
    url.searchParams.set(
      'link_to_tcam___customer_attention',
      userTenantName ?? '',
    );
    url.searchParams.set('email', userMeData?.email ?? '');
    return url.toString();
  }, [
    userMeData?.legalEntity.name,
    userMeData?.email,
    expenseCurrency?.isoCode,
  ]);

  const {
    amount: convertedBaseAmount,
    isLoading: isLoadingCurrencyConversionBaseAmount,
    isError: isCurrencyConversionErrorBaseAmount,
    isExchangeRateForCurrencyNotAvailableError:
      isExchangeRateNotAvailableErrorBaseAmount,
  } = useConvertCurrencies(
    baseAmount,
    expenseCurrency,
    'personal', // in expense details, we always need to use personal to get the correct exchange rates, even when on project expense page
    date,
    targetCurrency,
  );
  const formattedBaseAmount = useFormatNumber(baseAmount, {
    maximumFractionDigits: 0,
  });
  const formattedConvertedBaseAmount = useFormatNumber(
    convertedBaseAmount ?? 0,
  );

  const expenseAmountNumber = parseLocaleNumber(expenseAmount);
  const {
    amount: exchangedOriginalAmount,
    isLoading: isLoadingCurrencyConversionOriginalAmount,
    isError: isCurrencyConversionErrorOriginalAmount,
    isExchangeRateForCurrencyNotAvailableError:
      isExchangeRateNotAvailableErrorOriginalAmount,
  } = useConvertCurrencies(
    expenseAmountNumber,
    expenseCurrency,
    'personal', // in expense details, we always need to use personal to get the correct exchange rates, even when on project expense page
    date,
    targetCurrency,
  );
  const formattedExchangedOriginalAmount = useFormatNumber(
    exchangedOriginalAmount ?? 0,
  );

  if (!expenseCurrency || !targetCurrency || isLoadingUserMe) {
    return null;
  }

  const isCurrencyConversionError =
    isCurrencyConversionErrorBaseAmount ||
    isCurrencyConversionErrorOriginalAmount;

  const isLoadingCurrencyConversion =
    isLoadingCurrencyConversionBaseAmount ||
    isLoadingCurrencyConversionOriginalAmount;

  const displayExchangeRateNotAvailableError =
    isExchangeRateNotAvailableErrorBaseAmount ||
    isExchangeRateNotAvailableErrorOriginalAmount;

  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Icon
        name={
          !displayExchangeRateNotAvailableError ? 'currency_exchange' : 'info'
        }
        size={14}
        color={scssVariables.ColorFog80}
      />
      {displayExchangeRateNotAvailableError && (
        <Typography variant="label" color="secondary">
          {t('expenseDetails.CurrencyExchangeRateUnavailableError')}{' '}
          <Link
            href={requestExchangeRateLink()}
            underline="always"
            color="inherit"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('expenseDetails.CurrencyExchangeRateUnavailableErrorLink')}
          </Link>
        </Typography>
      )}
      {isCurrencyConversionError && !displayExchangeRateNotAvailableError && (
        <Typography variant="label" color="error" role="alert">
          {t('expenseDetails.LoadCurrencyConversionErrors')}
        </Typography>
      )}
      {isLoadingCurrencyConversion && (
        <Skeleton variant="text" width={120} height={16} />
      )}
      {!isLoadingCurrencyConversion && !isCurrencyConversionError && (
        <>
          <Stack direction="row" alignItems="center" gap={0}>
            <Typography variant="label" color="secondary" component="span">
              {formattedBaseAmount} {expenseCurrency?.value}
            </Typography>
            <Icon
              name={uiIcons.navigation.next}
              size={16}
              color={scssVariables.ColorFog80}
            />
            <Typography variant="label" color="secondary" component="span">
              {formattedConvertedBaseAmount} {targetCurrency?.value}
            </Typography>
          </Stack>
          {expenseAmount && (
            <Typography variant="label" color="secondary" component="span">
              {` = ${formattedExchangedOriginalAmount} ${targetCurrency?.value}`}
            </Typography>
          )}{' '}
        </>
      )}
    </Stack>
  );
};

interface ExpenseDetailsFormProps extends Record<string, unknown> {
  title: string;
  expenseType: ExpenseFormType;
  expenseTargetCurrency: CurrencyCodeSelectOption;
  formLocked?: boolean;
  isParentLoading?: boolean;
}

export const ExpenseDetailsForm = withFieldGroup({
  defaultValues: expenseDetailsDefaultValues,
  props: {
    // These default values are also for type-checking and are not used at runtime
    title: 'Title',
    expenseType: 'personal',
    expenseTargetCurrency: null,
    formLocked: undefined,
    isParentLoading: undefined,
  } as ExpenseDetailsFormProps,
  render: function Render({
    group,
    title,
    expenseType,
    expenseTargetCurrency,
    formLocked,
    isParentLoading,
  }) {
    const [
      targetCurrencyConvertedAmountLastChangedByUser,
      setTargetCurrencyConvertedAmountLastChangedByUser,
    ] = useState(false);

    const [createNewSupplierModalOpen, setCreateNewSupplierModalOpen] =
      useState(false);
    const [prefilledSupplierName, setPrefilledSupplierName] = useState('');

    const { t } = useTranslation(['forms', 'expenses']);

    const { setExpenseDetailValues } = useExpenseFormBlurredValuesStore();

    const selectedDate = useStore(group.store, (state) => state.values.date);
    const vatAmount = useStore(group.store, (state) => state.values.vatAmount);
    const amountIncludingVat = useStore(
      group.store,
      (state) => state.values.amountIncludingVat,
    );
    const targetCurrencyConvertedAmount = useStore(
      group.store,
      (state) => state.values.targetCurrencyConvertedAmount,
    );
    const selectedCurrency = useStore(
      group.store,
      (state) => state.values.expenseCurrency,
    );

    const selectedExpenseType = useStore(
      group.store,
      (state) => state.values.expenseType,
    );

    const selectedSupplier = useStore(
      group.store,
      (state) => state.values.supplier,
    );

    const decimalFormattedVatAmount = useFormatNumber(
      parseLocaleNumber(vatAmount),
    );
    const decimalFormattedAmountIncludingVat = useFormatNumber(
      parseLocaleNumber(amountIncludingVat),
    );
    const decimalFormattedTargetCurrencyConvertedAmount = useFormatNumber(
      parseLocaleNumber(targetCurrencyConvertedAmount || ''),
    );

    const {
      amount: autoCalculatedTargetCurrencyConvertedAmount,
      isLoading: isLoadingAutoCalculatedTargetCurrencyConvertedAmount,
    } = useConvertCurrencies(
      parseLocaleNumber(amountIncludingVat),
      selectedCurrency,
      'personal', // in expense details, we always need to use personal to get the correct exchange rates, even when on project expense page
      selectedDate,
      expenseTargetCurrency,
    );

    const autoCalculatedFormattedTargetCurrencyConvertedAmount =
      useFormatNumber(autoCalculatedTargetCurrencyConvertedAmount ?? NaN);

    const resetButtonIsDisabled =
      autoCalculatedFormattedTargetCurrencyConvertedAmount ===
      targetCurrencyConvertedAmount;

    const resetTargetCurrencyConvertedAmount = () => {
      group.setFieldValue(
        'targetCurrencyConvertedAmount',
        autoCalculatedFormattedTargetCurrencyConvertedAmount,
      );
      setExpenseDetailValues({
        targetCurrencyConvertedAmount:
          autoCalculatedFormattedTargetCurrencyConvertedAmount,
      });
      setTargetCurrencyConvertedAmountLastChangedByUser(false);
    };

    const { isLoading: isUserMeLoading } = useGetUserMe(); // on new expense, we want the user's currency as default so we await the loading here

    const showTargetCurrencyConvertedAmountField =
      !!selectedCurrency &&
      expenseTargetCurrency?.value !== selectedCurrency?.value;

    const { hasFeature: showIsBillableCheckbox } =
      useUserAccountHasFeature('billableExpense');
    const { hasFeature: showSupplierField } =
      useUserAccountHasFeature('supplierOnExpenses');
    const { settingValue: showSupplierFieldOnPersonalExpenseSetting } =
      useUserAccountSettingValue('showSupplierInvoiceNoOnPersonalExpense');
    const showSupplierInvoiceNumberField =
      expenseType === 'personal'
        ? showSupplierField &&
          !!showSupplierFieldOnPersonalExpenseSetting &&
          !!selectedSupplier
        : showSupplierField && !!selectedSupplier;

    const { hasFeature: expenseNoSetting } =
      useUserAccountHasFeature('expenseNo');
    const { hasFeature: isExpenseNoAutoGeneratedSetting } =
      useUserAccountHasFeature('isExpenseNoAutoGenerated');

    const showExpenseNumberField =
      expenseNoSetting && !isExpenseNoAutoGeneratedSetting;

    const isDataLoading = isUserMeLoading || isParentLoading;

    const fieldsDisabled = formLocked || isDataLoading;

    const [
      targetCurrencyConvertedAmountPendingUpdate,
      setTargetCurrencyConvertedAmountPendingUpdate,
    ] = useState(false);

    const updateTargetCurrencyConvertedAmount = (
      origin: 'amount' | 'currency' | 'date',
    ) => {
      if (origin === 'currency' || origin === 'date') {
        // a currency or date change always triggers an update of the converted amount (might change that depending on feedback)
        if (amountIncludingVat !== '') {
          setTargetCurrencyConvertedAmountPendingUpdate(true);
        }
      } else if (origin === 'amount') {
        // amount change only triggers update if user has not manually changed converted amount
        if (!targetCurrencyConvertedAmountLastChangedByUser) {
          setTargetCurrencyConvertedAmountPendingUpdate(true);
        }
      }
    };

    useEffect(() => {
      /*
       * update converted amount only when there is a pending update set to prevent instant update on change of amount
       * (basically manually do onBlur behavior with useEffect)
       */
      if (targetCurrencyConvertedAmountPendingUpdate) {
        if (isLoadingAutoCalculatedTargetCurrencyConvertedAmount) {
          group.setFieldValue('targetCurrencyConvertedAmount', '');
          setExpenseDetailValues({
            targetCurrencyConvertedAmount: '',
          });
          return;
        }

        if (
          autoCalculatedFormattedTargetCurrencyConvertedAmount ||
          autoCalculatedTargetCurrencyConvertedAmount === 0 // check by int value here to not have to worry about decimal formatting differences
        ) {
          group.setFieldValue(
            'targetCurrencyConvertedAmount',
            autoCalculatedFormattedTargetCurrencyConvertedAmount,
          );
          setExpenseDetailValues({
            targetCurrencyConvertedAmount:
              autoCalculatedFormattedTargetCurrencyConvertedAmount,
          });
          setTargetCurrencyConvertedAmountPendingUpdate(false);
        }
      }
    }, [
      autoCalculatedFormattedTargetCurrencyConvertedAmount,
      isLoadingAutoCalculatedTargetCurrencyConvertedAmount,
      targetCurrencyConvertedAmountPendingUpdate,
      autoCalculatedTargetCurrencyConvertedAmount,
    ]);

    useEffect(() => {
      /*
       * when expense target currency changes, we need to recalculate the converted amount,
       * but only when the form is already changed by the user, as the first hook call is coming from setting the default value or initial edit value
       */
      if (!!expenseTargetCurrency && !expenseFormIsUnchanged) {
        updateTargetCurrencyConvertedAmount('currency');
      }
    }, [expenseTargetCurrency]);

    const { settingValue: companyDefaultVAT } =
      useUserAccountSettingValue('defaultVAT');

    const [calculatedVatAmount, setCalculatedVatAmount] = useState(-1); // initially use -1 to distinguish between not yet calculated and 0 VAT when VAT rate 0
    const decimalFormattedCalculatedVatAmount =
      useFormatNumber(calculatedVatAmount);

    const updateVatAmount = (newExpenseType?: ExpenseTypeSelectOption) => {
      const expenseType = newExpenseType ? newExpenseType : selectedExpenseType;
      if (amountIncludingVat) {
        const vatRate =
          expenseType && expenseType.vat !== undefined
            ? expenseType.vat
            : (companyDefaultVAT ?? 0);
        const amountIncVat = parseLocaleNumber(amountIncludingVat);

        const newVatAmount = amountIncVat - amountIncVat / (1 + vatRate / 100);

        setCalculatedVatAmount(newVatAmount);
      }
    };

    // use useEffect to make sure formatted amount has already recalculated
    useEffect(() => {
      if (selectedExpenseType || amountIncludingVat !== '') {
        group.setFieldValue('vatAmount', decimalFormattedCalculatedVatAmount);
        setExpenseDetailValues({
          vatAmount: decimalFormattedCalculatedVatAmount,
        });
      }
    }, [decimalFormattedCalculatedVatAmount]);

    const currentSelectedContract = useStore(
      group.form.store,
      (state) =>
        (
          state.values as {
            project_information: ProjectInformationFormFields;
          }
        )?.project_information?.contract,
    );

    const selectedContractIsFixedPrice = currentSelectedContract
      ? currentSelectedContract.isFixedPriceContract
      : false;

    const selectedEmployee = useStore(
      group.form.store,
      (state) =>
        (
          state.values as {
            project_information: ProjectInformationFormFields;
          }
        )?.project_information?.employee,
    );

    const selectedProject = useStore(
      group.form.store,
      (state) =>
        (
          state.values as {
            project_information: ProjectInformationFormFields;
          }
        )?.project_information?.project,
    );

    const handleCreateNewSupplierClicked = (supplierName?: string) => {
      setCreateNewSupplierModalOpen(true);
      setPrefilledSupplierName(supplierName ?? '');
    };

    const expenseFormIsUnchanged = useStore(
      group.form.store,
      (state) => state.isDefaultValue,
    );

    return (
      <>
        <group.FormFieldset legend={title} filled>
          <Stack gap={2}>
            <group.FormGrid>
              {isDataLoading ? (
                [1, 2, 3, 4, 5, 6].map((index) => (
                  <InputFieldSkeletonLoader key={index} />
                ))
              ) : (
                <>
                  <group.AppField
                    name="date"
                    listeners={{
                      onChange: () => {
                        updateTargetCurrencyConvertedAmount('date');
                      },
                    }}
                  >
                    {(Field) => (
                      <Field.DateInputField
                        required={isRequired(expenseDetailsSchema.shape.date)}
                        label={t('labels.Date')}
                        disabled={fieldsDisabled}
                      />
                    )}
                  </group.AppField>
                  {showSupplierField && (
                    <Grid container spacing={1}>
                      <Grid size={showSupplierInvoiceNumberField ? 7 : 12}>
                        <group.AppField name="supplier">
                          {(Field) => (
                            <Field.SelectInputField
                              required={isRequired(
                                expenseDetailsSchema.shape.supplier,
                              )}
                              label={t('labels.Supplier')}
                              disabled={fieldsDisabled}
                              searchFunction={getSearchSupplierSelectOptions}
                              typeSenseCollection={
                                TypesenseCollection.SUPPLIERS_VIEW
                              }
                              getOptionByIdFallbackFunction={
                                getSupplierSelectOptionById
                              }
                              onCreateNewOptionClick={
                                handleCreateNewSupplierClicked
                              }
                            />
                          )}
                        </group.AppField>
                      </Grid>
                      {showSupplierInvoiceNumberField && (
                        <Grid size={5}>
                          <group.AppField name="supplierInvoiceNumber">
                            {(Field) => (
                              <Field.TextInputField
                                required={isRequired(
                                  expenseDetailsSchema.shape
                                    .supplierInvoiceNumber,
                                )}
                                label={t('labels.SupplierInvoiceNumber')}
                                disabled={fieldsDisabled}
                              />
                            )}
                          </group.AppField>
                        </Grid>
                      )}
                    </Grid>
                  )}
                  <group.AppField name="paymentMethod">
                    {(Field) => (
                      <Field.SelectInputField
                        required={isRequired(
                          expenseDetailsSchema.shape.paymentMethod,
                        )}
                        label={t('labels.PaymentMethod')}
                        disabled={fieldsDisabled}
                        searchFunction={getSearchPaymentMethodSelectOptions}
                        typeSenseCollection={
                          TypesenseCollection.PAYMENT_METHODS
                        }
                        searchOptions={{
                          filterBy:
                            expenseType === 'project'
                              ? `IsPaidByEmployee:false`
                              : undefined,
                        }}
                        getOptionByIdFallbackFunction={
                          getPaymentMethodSelectOptionById
                        }
                      />
                    )}
                  </group.AppField>
                  <Grid container spacing={1}>
                    <Grid size={7}>
                      <group.AppField
                        name="amountIncludingVat"
                        listeners={{
                          onBlur: ({ value }) => {
                            //  allow value = 0 here to correctly trigger recalculation of other fields
                            if (
                              (group.getFieldMeta('amountIncludingVat')
                                ?.isValid ||
                                parseLocaleNumber(value) === 0) &&
                              group.getFieldMeta('amountIncludingVat')?.isDirty
                            ) {
                              group.setFieldValue(
                                'amountIncludingVat',
                                decimalFormattedAmountIncludingVat,
                              );
                              updateTargetCurrencyConvertedAmount('amount');
                              updateVatAmount();

                              setExpenseDetailValues({
                                amountIncludingVat:
                                  decimalFormattedAmountIncludingVat,
                              });
                            }
                          },
                        }}
                      >
                        {(Field) => (
                          <Field.TextInputField
                            required={isRequired(
                              expenseDetailsSchema.shape.amountIncludingVat,
                            )}
                            type="number"
                            label={t('labels.AmountIncludingVat')}
                            disabled={fieldsDisabled}
                          />
                        )}
                      </group.AppField>
                    </Grid>
                    <Grid size={5}>
                      <group.AppField
                        name="expenseCurrency"
                        listeners={{
                          onChange: () => {
                            updateTargetCurrencyConvertedAmount('currency');
                          },
                        }}
                      >
                        {(Field) => (
                          <Field.SelectInputField
                            required={isRequired(
                              expenseDetailsSchema.shape.expenseCurrency,
                            )}
                            label={t('labels.Currency')}
                            disabled={fieldsDisabled}
                            searchFunction={getSearchCurrencyCodeSelectOptions}
                            typeSenseCollection={
                              TypesenseCollection.CURRENCY_CODES
                            }
                            getOptionByIdFallbackFunction={
                              getCurrencyCodeSelectOptionById
                            }
                          />
                        )}
                      </group.AppField>
                    </Grid>
                  </Grid>
                  <group.AppField
                    name="vatAmount"
                    listeners={{
                      onBlur: () => {
                        if (
                          group.getFieldMeta('vatAmount')?.isValid &&
                          group.getFieldMeta('vatAmount')?.isDirty
                        ) {
                          group.setFieldValue(
                            'vatAmount',
                            decimalFormattedVatAmount,
                          );
                          setExpenseDetailValues({
                            vatAmount: decimalFormattedVatAmount,
                          });
                        }
                      },
                    }}
                  >
                    {(Field) => (
                      <Field.TextInputField
                        required={isRequired(
                          expenseDetailsSchema.shape.vatAmount,
                        )}
                        type="number"
                        label={t('labels.VatAmount')}
                        endAdornment={
                          <InputAdornment position="end">
                            {selectedCurrency?.value}
                          </InputAdornment>
                        }
                        disabled={fieldsDisabled}
                      />
                    )}
                  </group.AppField>
                  {showTargetCurrencyConvertedAmountField && (
                    <Stack direction="row" alignItems="flex-start" gap={1}>
                      <group.AppField
                        name="targetCurrencyConvertedAmount"
                        listeners={{
                          onBlur: ({ value }) => {
                            if (
                              group.getFieldMeta(
                                'targetCurrencyConvertedAmount',
                              )?.isValid &&
                              group.getFieldMeta(
                                'targetCurrencyConvertedAmount',
                              )?.isDirty
                            ) {
                              if (
                                group.getFieldMeta('amountIncludingVat')
                                  ?.isValid &&
                                !targetCurrencyConvertedAmount
                              ) {
                                group.setFieldValue(
                                  'targetCurrencyConvertedAmount',
                                  autoCalculatedFormattedTargetCurrencyConvertedAmount,
                                );
                                setExpenseDetailValues({
                                  targetCurrencyConvertedAmount:
                                    autoCalculatedFormattedTargetCurrencyConvertedAmount,
                                });
                              } else {
                                group.setFieldValue(
                                  'targetCurrencyConvertedAmount',
                                  decimalFormattedTargetCurrencyConvertedAmount,
                                );
                                setExpenseDetailValues({
                                  targetCurrencyConvertedAmount:
                                    decimalFormattedTargetCurrencyConvertedAmount,
                                });
                              }
                            }
                            if (
                              !targetCurrencyConvertedAmountLastChangedByUser &&
                              value !==
                                autoCalculatedFormattedTargetCurrencyConvertedAmount
                            ) {
                              setTargetCurrencyConvertedAmountLastChangedByUser(
                                true,
                              );
                            }
                          },
                        }}
                      >
                        {(Field) => (
                          <Field.TextInputField
                            required={isRequired(
                              expenseDetailsSchema.shape
                                .targetCurrencyConvertedAmount,
                            )}
                            type="number"
                            label={t('labels.ConvertedAmount')}
                            infoTooltip={t(
                              'expenses:expenseDetails.ConvertedAmountInfoTooltip',
                            )}
                            endAdornment={
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={0}
                              >
                                {isLoadingAutoCalculatedTargetCurrencyConvertedAmount && (
                                  <CircularProgress
                                    size={18}
                                    color="inherit"
                                    thickness={4}
                                  />
                                )}
                                <InputAdornment position="end">
                                  {expenseTargetCurrency?.value}
                                </InputAdornment>
                              </Stack>
                            }
                            helperText={
                              <TargetCurrencyConvertedAmountHelperText
                                expenseAmount={amountIncludingVat}
                                expenseCurrency={selectedCurrency}
                                date={selectedDate}
                                targetCurrency={expenseTargetCurrency}
                              />
                            }
                            disabled={fieldsDisabled}
                          />
                        )}
                      </group.AppField>
                      <div className={styles.ResetButtonWrapper}>
                        <IconButton
                          iconName={uiIcons.actions.reset}
                          tooltip={t(
                            'expenses:expenseDetails.ResetConvertedAmountTooltip',
                          )}
                          size="small"
                          disabled={resetButtonIsDisabled}
                          disableTooltip={resetButtonIsDisabled}
                          onClick={resetTargetCurrencyConvertedAmount}
                        />
                      </div>
                    </Stack>
                  )}
                  <group.AppField
                    name="expenseType"
                    listeners={{
                      onChange: ({ value }) => {
                        // when expense type changes, update calculated VAT amount with the new value
                        updateVatAmount(value as ExpenseTypeSelectOption);
                      },
                    }}
                  >
                    {(Field) => (
                      <Field.SelectInputField
                        required={isRequired(
                          expenseDetailsSchema.shape.expenseType,
                        )}
                        label={t('labels.ExpenseType')}
                        disabled={
                          fieldsDisabled ||
                          (expenseType === 'project' && !selectedProject)
                        }
                        disabledTooltip={
                          !fieldsDisabled &&
                          expenseType === 'project' &&
                          !selectedProject
                            ? t(
                                'expenses:expenseDetails.ExpenseTypesDisabledTooltip',
                              )
                            : undefined
                        }
                        searchFunction={getSearchExpenseTypeSelectOptions}
                        typeSenseCollection={TypesenseCollection.EXPENSE_TYPES}
                        getOptionByIdFallbackFunction={
                          getExpenseTypeSelectOptionById
                        }
                        searchOptions={{
                          filterBy:
                            expenseType === 'personal'
                              ? selectedEmployee &&
                                selectedEmployee.legalEntityId
                                ? `LegalEntityId:${selectedEmployee.legalEntityId}`
                                : undefined
                              : expenseType === 'project'
                                ? selectedProject &&
                                  selectedProject.legalEntityId
                                  ? `LegalEntityId:${selectedProject.legalEntityId}`
                                  : undefined
                                : undefined,
                        }}
                        dependsOnFields={
                          expenseType === 'project'
                            ? ['project_information.project']
                            : ['project_information.employee']
                        }
                      />
                    )}
                  </group.AppField>
                </>
              )}
            </group.FormGrid>
            {showExpenseNumberField && (
              <group.FormGrid>
                <>
                  {isDataLoading ? (
                    <InputFieldSkeletonLoader />
                  ) : (
                    <group.AppField name="expenseNumber">
                      {(Field) => (
                        <Field.TextInputField
                          required={isRequired(
                            expenseDetailsSchema.shape.expenseNumber,
                          )}
                          type="text"
                          label={t('labels.ExpenseNumber')}
                          disabled={fieldsDisabled}
                        />
                      )}
                    </group.AppField>
                  )}
                </>
              </group.FormGrid>
            )}
            {showIsBillableCheckbox && (
              <div
                className={cx(styles.IsBillableWrapper, {
                  [styles.HasExpenseNumber]: showExpenseNumberField,
                })}
              >
                {isDataLoading ? (
                  <Skeleton variant="rounded" width={100} height={20} />
                ) : (
                  <group.AppField name="billable">
                    {(Field) => (
                      // can't use isRequired() here as marking a boolean optional conflicts with its type
                      <Field.CheckboxField
                        required={false}
                        label={t('labels.Billable')}
                        disabled={
                          fieldsDisabled || selectedContractIsFixedPrice
                        }
                      />
                    )}
                  </group.AppField>
                )}
              </div>
            )}
            <group.AppField name="expenseComment">
              {(Field) => (
                <Field.TextareaInputField
                  required={isRequired(
                    expenseDetailsSchema.shape.expenseComment,
                  )}
                  label={t('labels.ExpenseComment')}
                  disabled={fieldsDisabled}
                />
              )}
            </group.AppField>
          </Stack>
        </group.FormFieldset>

        <CreateNewSupplierModal
          open={createNewSupplierModalOpen}
          prefilledSupplierName={prefilledSupplierName}
          onClose={() => setCreateNewSupplierModalOpen(false)}
          onCreateNewSupplierSuccess={(newSupplier) => {
            group.setFieldValue('supplier', newSupplier);
          }}
        />
      </>
    );
  },
});
